// Client extension point: where the installed desktop client authenticates, and which control
// plane it talks to. These values are baked into the desktop build (the packaged app reads them
// from its bundled env file), so they are per-customer configuration, not product constants.
//
// IDENTITY-PROVIDER AGNOSTIC BY CONSTRUCTION. There is deliberately no `kind: 'entra' | 'oidc'`
// enum: an OIDC issuer URL is the general case, and Entra is a shortcut where the issuer is
// derived from the tenant id. The first customer deployment uses Keycloak, so treating Entra as
// the only shape — or as one of a fixed list — would make every new provider a schema change.
//
// Why this exists: without it the packaged client falls back to the identifiers baked into the
// vendored fork's `resources/echowork.env`, which point at OUR tenant and OUR PoC control plane.
// Measured on the reference distribution: the installer built from it logged into the real Entra
// and never touched the customer's own stack.
//
// STRICT ON PURPOSE, like every extension-point schema: a typo must fail `validate` loudly.
import { z } from 'zod';
/**
 * Where the client authenticates. Exactly one of `authorityUrl` and `entraTenantId` — an issuer
 * URL for any OIDC provider, or the Entra tenant whose authority the tool derives.
 */
export const IdentityProviderSchema = z
    .strictObject({
    /**
     * OIDC issuer (realm) URL, e.g. `https://kc.customer.example/realms/echowork`. The general
     * case: the client runs its PKCE flow against this issuer's discovery metadata.
     *
     * With a non-Entra issuer the token's `aud` must carry `clientApiClientId` — on Keycloak an
     * audience mapper does that. The client does NOT request an `api://…` scope on this path:
     * measured, Keycloak rejects any scope its realm does not declare, and declaring one in the
     * realm is not a workable answer (a realm that declares `clientScopes` replaces Keycloak's
     * built-ins, so `profile` and `offline_access` stop resolving).
     *
     * https only, with NO localhost exemption: measured on msal-node 3.8.10, an http authority
     * throws `authority_uri_insecure` before any request is made, `http://localhost` included.
     * An http issuer is a config that cannot log in, so it is rejected where it is written.
     * (The local trial's Entra mock serves https — that is what its certificate script is for.)
     */
    authorityUrl: z.url({ protocol: /^https$/ }).optional(),
    /** Entra tenant id — the shortcut. The tool derives the authority from it. */
    entraTenantId: z.string().min(1).optional(),
    /** Public client id the desktop logs in with (PKCE, no secret). */
    desktopClientId: z.string().min(1),
    /**
     * Client id the control plane accepts as the token audience for the client surface
     * (`/api/me/*`). Defaults to `desktopClientId`, which is the common case: one app
     * registration is both the login client and the API resource.
     */
    clientApiClientId: z.string().min(1).optional(),
})
    .refine((value) => (value.authorityUrl === undefined) !== (value.entraTenantId === undefined), 'exactly one of authorityUrl and entraTenantId must be set — an OIDC issuer URL, or an Entra tenant to derive it from');
/** The client-side coordinates of one distribution. */
export const ClientConfigSchema = z.strictObject({
    identityProvider: IdentityProviderSchema,
    /**
     * Base URL of the control plane the client calls (`/api/me/*`). http or https by the schema;
     * the validate step narrows http to localhost, which is what the local trial profile uses.
     */
    controlPlaneUrl: z.url({ protocol: /^https?$/ }),
});
//# sourceMappingURL=client.js.map